import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * useFreezeOnAgentRun — derives "is the editor's chat agent currently running"
 * state at the route level WITHOUT directly invoking the SDK's
 * `useConversationFlow()` hook (which requires being inside the
 * `<ChatRuntimeProvider>` tree that lives inside `<AgentApp>`).
 *
 * Pattern mirrors useEditorSend: the chat panel (which IS inside the SDK
 * provider) calls `registerFlow({ isLoading })` whenever the conversation
 * flow state changes. This hook tracks the latest value and computes:
 *  - `isAgentRunning` — true while a turn is in flight
 *  - `runJustEnded` — pulse-true on the in-progress→idle edge so consumers
 *    can fire a refetch / animation once per run completion
 */

export interface UseFreezeOnAgentRunResult {
  isAgentRunning: boolean
  runJustEnded: boolean
  /** Pass to <ChatPanel onRegisterFlow={…}> to wire the SDK state in. */
  registerFlow: (state: { isLoading: boolean }) => void
}

export function useFreezeOnAgentRun(): UseFreezeOnAgentRunResult {
  const [isAgentRunning, setIsAgentRunning] = useState(false)
  const [runJustEnded, setRunJustEnded] = useState(false)
  const lastLoading = useRef(false)

  const registerFlow = useCallback((state: { isLoading: boolean }) => {
    if (lastLoading.current && !state.isLoading) {
      setRunJustEnded(true)
    }
    lastLoading.current = state.isLoading
    setIsAgentRunning(state.isLoading)
  }, [])

  // Reset the runJustEnded latch on the next tick so consumers see it once.
  useEffect(() => {
    if (!runJustEnded) return
    const t = setTimeout(() => setRunJustEnded(false), 0)
    return () => clearTimeout(t)
  }, [runJustEnded])

  return { isAgentRunning, runJustEnded, registerFlow }
}
